import { motion } from "framer-motion";
import {
  Clock, Shirt, Bell, Gift, Wine, Gamepad2, Baby, GlassWater, Flame, CakeSlice,
  Sparkles, ConciergeBell, UserRoundCheck, Apple, Percent, Landmark, UtensilsCrossed, Sun, Waves, Bath
} from "lucide-react";

const perks = [
  { icon: Clock, label: "Early check-in / late check-out" },
  { icon: Shirt, label: "Complimentary laundry & ironing" },
  { icon: Bell, label: "24-hour room service" },
  { icon: Gift, label: "Nightly turndown with a gift" },
  { icon: Wine, label: "Daily mini-bar replenishment" },
  { icon: Gamepad2, label: "Video game console in room" },
  { icon: Baby, label: "Babysitter on request" },
  { icon: GlassWater, label: "Welcome cocktails on arrival" },
  { icon: Flame, label: "Romantic dinner on the beach" },
  { icon: CakeSlice, label: "Wedding · anniversary · birthday dinner" },
  { icon: Sparkles, label: "Access to spa sanctuary" },
  { icon: ConciergeBell, label: "Dedicated concierge" },
  { icon: UserRoundCheck, label: "Personal butler" },
  { icon: Apple, label: "Fruit basket in room" },
  { icon: Percent, label: "25% off resort shops" },
  { icon: Percent, label: "25% off spa treatments" },
  { icon: Landmark, label: "50% off golf course" },
  { icon: UtensilsCrossed, label: "Priority themed-restaurant access" },
  { icon: Sun, label: "Sunbeds reserved · exclusive beach" },
  { icon: Waves, label: "Access to gold facilities" },
  { icon: Bath, label: "Robes · slippers · flip flops · beach towel" },
  { icon: Sparkles, label: "Exclusive amenities · Dominican Republic" },
];

export default function Perks() {
  return (
    <section id="perks" className="relative bg-[#F2ECE0] py-24 md:py-40 z-[2]" data-testid="perks-section">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-12 gap-6 mb-16 md:mb-20">
          <div className="col-span-12 md:col-span-6">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-8 bg-[#C5A059]" />
              <span className="eyebrow text-[#C5A059]">The Complete List · 22 Perks</span>
            </div>
            <h2 className="font-serif-editorial text-5xl md:text-7xl leading-[0.95] tracking-tight">
              The perks<br /><span className="italic">members pay for.</span>
            </h2>
          </div>
          <div className="col-span-12 md:col-span-5 md:col-start-8 flex md:items-end">
            <p className="text-[#1A1A1A]/75 leading-relaxed">
              Twenty-two amenities normally reserved for repeat guests, high-tier loyalty tiers, or membership-only programs. Included with every trip we build.
            </p>
          </div>
        </div>

        {/* Spotlight image + list layout */}
        <div className="grid grid-cols-12 gap-6 md:gap-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }} whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }} transition={{ duration: 1.1, ease: [0.19, 1, 0.22, 1] }}
            className="col-span-12 md:col-span-5 md:sticky md:top-24 self-start"
          >
            <div className="relative overflow-hidden aspect-[3/4] clip-arch-tall bg-[#1A1A1A]">
              <img
                alt="Exclusive romantic dinner set by The Elevated Escape — champagne, rose petals, and napkin swans."
                src="/perks-dinner-edited.jpg"
                style={{ filter: "saturate(0.78) contrast(0.94) brightness(0.98) sepia(0.14)" }}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-[#E6C5C0] mix-blend-soft-light opacity-45 pointer-events-none" />
              <div className="absolute inset-0 bg-gradient-to-b from-[#F9F6F0]/10 via-transparent to-transparent pointer-events-none" />
              <div className="absolute inset-x-0 bottom-0 p-6 text-[#F9F6F0]">
                <p className="eyebrow text-[#E4D3A8] mb-2">Real Trip · Signature Ritual</p>
                <p className="font-serif-editorial italic text-2xl md:text-3xl leading-tight">The Exclusive dinner. Set for two, by us, before you arrive.</p>
              </div>
            </div>
          </motion.div>

          <div className="col-span-12 md:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-x-8">
            {perks.map((p, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                transition={{ duration: 0.6, delay: (i % 10) * 0.03 }}
                className="flex items-start gap-4 py-4 border-b border-black/10"
                data-testid={`perk-${i}`}
              >
                <p.icon size={18} strokeWidth={1.25} className="text-[#C5A059] mt-1 shrink-0" />
                <span className="text-[#1A1A1A] text-[15px] leading-relaxed">{p.label}</span>
              </motion.div>
            ))}
          </div>
        </div>

        <p className="mt-16 md:mt-20 text-xs uppercase tracking-[0.22em] text-[#5C5C5C]">
          * Denotes perks subject to resort and reservation timing. All confirmed at itinerary sign-off.
        </p>
      </div>
    </section>
  );
}
